<template>
  <div :style="mapStyle">
    <div ref="map" id="map-canvas"></div>
  </div>
</template>

<script>
import { RotateIcon, bearing } from "../../utility/utility";

export default {
  name: "Directions",
  props: {
    inTransit: {
      type: Boolean,
      default: true
    },
    origin: {
      type: Object,
      required: true
    },
    destination: {
      type: Object,
      required: true
    },
    latestPoint: {
      type: Object
    },
    movements: {
      type: Object
    },
    width: {
      type: Number
    },
    height: {
      type: Number
    },
    heightUnits: {
      type: String,
      default: "px"
    },
    maxZoom: {
      type: Number
    },
    noTooltips: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      parsedWayPoints: [],
      directionsService: null,
      pickupCo: null,
      deliveryCo: null,
      preserveViewport: false,
      map: null
    };
  },
  methods: {
    initMap() {
      this.pickupCo = new google.maps.LatLng(this.origin.lat, this.origin.lng);

      this.deliveryCo = new google.maps.LatLng(
        this.destination.lat,
        this.destination.lng
      );

      const mapOptions = {
        zoom: 7,
        mapTypeId: "roadmap",
        maxZoom: this.maxZoom ? this.maxZoom : null,
        disableDefaultUI: true,
        center: { lat: -41.847341, lng: 173.052757 }
      };
      this.map = new google.maps.Map(this.$refs.map, mapOptions);
      google.maps.event.trigger(this.map, "resize");
      this.map.panBy(0, 0);

      var bounds = new google.maps.LatLngBounds();

      if (this.hasLatestPoint && this.inTransit) {
        const truckCo = new google.maps.LatLng(
          this.latestPoint.lat,
          this.latestPoint.lng
        );
        bounds.extend(truckCo);
        this.preserveViewport = true;
      } else {
        bounds.extend(this.pickupCo);
      }
      bounds.extend(this.deliveryCo);
      this.map.fitBounds(bounds);

      this.directionsService = new google.maps.DirectionsService();
      this.dDisplayMoved = new google.maps.DirectionsRenderer({
        map: this.map,
        markerOptions: {
          clickable: !this.noTooltips
        },
        polylineOptions: {
          strokeColor: "#1EA362",
          strokeOpacity: 0.6,
          strokeWeight: 6
        },
        suppressMarkers: true
      });
      this.dDisplayOngoing = new google.maps.DirectionsRenderer({
        map: this.map,
        markerOptions: {
          clickable: !this.noTooltips
        },
        polylineOptions: {
          strokeColor: "#4A89F3",
          strokeOpacity: 0.6,
          strokeWeight: 6
        },
        suppressMarkers: true
      });

      this.displayStopMarkers(this.map);

      const {
        movedStartCo,
        movedEndCo,
        movedWayPoints,
        ongoingStartCo,
        ongoingEndCo,
        ongoingWayPoints
      } = this.assignCoordinates();

      this.drawDirections(
        movedStartCo,
        movedEndCo,
        movedWayPoints,
        ongoingStartCo,
        ongoingEndCo,
        ongoingWayPoints
      );

      if (this.hasLatestPoint && this.inTransit) {
        const courseOverGround = bearing(
          this.latestPoint.lat,
          this.latestPoint.lng,
          this.deliveryCo.lat(),
          this.deliveryCo.lng()
        );
        if (this.movements.data[this.movements.data.length - 1].type == 19) {
          this.setIcon(
            "/images/truck_icon_redv1.svg",
            this.map,
            courseOverGround
          );
        } else {
          this.setIcon(
            "/images/truck_icon_greenv1.svg",
            this.map,
            courseOverGround
          );
        }
      }

      new google.maps.Marker({
        position: this.pickupCo,
        icon: "/images/pickup-map-marker-32px-green.png",
        map: this.map
      });

      new google.maps.Marker({
        position: this.deliveryCo,
        icon: "/images/delivery-map-marker-32px-red.png",
        map: this.map
      });
    },
    setIcon(iconString, map, courseOverGround = bearing(0, 0, 0, 0)) {
      RotateIcon.makeIcon(iconString)
        .then(rotateIcon => {
          const url = rotateIcon
            .setRotation({ deg: courseOverGround })
            .getUrl();
          const truckIcon = {
            url: url,
            scaledSize: new google.maps.Size(60, 60),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(30, 30)
          };
          new google.maps.Marker({
            position: this.latestPoint,
            icon: truckIcon,
            map: map
          });
        })
        .catch(e => console.log(e));
    },
    assignCoordinates() {
      if (this.hasLatestPoint) {
        const latestCo = new google.maps.LatLng(
          this.latestPoint.lat,
          this.latestPoint.lng
        );
        if (this.inTransit) {
          return {
            movedStartCo: this.pickupCo,
            movedEndCo: latestCo,
            movedWayPoints: this.slicedWayPoints,
            ongoingStartCo: latestCo,
            ongoingEndCo: this.deliveryCo,
            ongoingWayPoints: []
          };
        }
        return {
          movedStartCo: this.pickupCo,
          movedEndCo: this.deliveryCo,
          movedWayPoints: this.slicedWayPoints,
          ongoingStartCo: null,
          ongoingEndCo: null,
          ongoingWayPoints: []
        };
      }

      if (this.inTransit) {
        return {
          movedStartCo: null,
          movedEndCo: null,
          movedWayPoints: [],
          ongoingStartCo: this.pickupCo,
          ongoingEndCo: this.deliveryCo,
          ongoingWayPoints: this.slicedWayPoints
        };
      }
      return {
        movedStartCo: this.pickupCo,
        movedEndCo: this.deliveryCo,
        movedWayPoints: this.slicedWayPoints,
        ongoingStartCo: null,
        ongoingEndCo: null
      };
    },
    drawDirections(
      movedStart,
      movedEnd,
      movedWayPoints,
      ongoingStart,
      ongoingEnd,
      ongoingWayPoints
    ) {
      if (movedStart !== null && movedEnd !== null) {
        this.calculateAndDisplayRoute(
          this.directionsService,
          this.dDisplayMoved,
          movedStart,
          movedEnd,
          movedWayPoints
        );
      }

      if (ongoingStart !== null && ongoingEnd !== null) {
        this.calculateAndDisplayRoute(
          this.directionsService,
          this.dDisplayOngoing,
          ongoingStart,
          ongoingEnd,
          ongoingWayPoints
        );
      }
    },
    calculateAndDisplayRoute(
      directionsService,
      directionsDisplay,
      start,
      end,
      waypoints = []
    ) {
      const request = {
        origin: start,
        destination: end,
        avoidTolls: true,
        avoidHighways: false,
        travelMode: google.maps.TravelMode.DRIVING,
        waypoints: waypoints,
        optimizeWaypoints: true
      };
      directionsService.route(request, (response, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          directionsDisplay.setOptions({
            preserveViewport: this.preserveViewport
          });
          directionsDisplay.setDirections(response);
        } else {
          console.log("Directions request failed due to " + status);
          console.log(response);
        }
      });
    },
    displayStopMarkers(map) {
      if (this.hasMovements) {
        this.movements.data.forEach(e => {
          if (e.point != null) {
            if (e.type_id === 9) {
              return;
            }

            let infoWindowText = "Transit stop detected at " + e.name;
            if (e.type_id === 7) {
              infoWindowText = "Tracker Linked To Booking";
            } else if (e.type_id === 10) {
              infoWindowText = "Tracker Unlinked From Booking";
            }
            const marker = new google.maps.Marker({
              position: new google.maps.LatLng(
                e.point.coordinates[1],
                e.point.coordinates[0]
              ),
              map: map,
              animation: google.maps.Animation.DROP,
              clickable: true,
              title: e.name,
              label: "M",
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 8.5,
                fillColor: "#F00",
                fillOpacity: 0.4,
                strokeWeight: 0.4
              }
            });
            const infoWindow = new google.maps.InfoWindow({
              content: "<div>" + infoWindowText + "</div>"
            });
            marker.addListener("click", function() {
              infoWindow.open(map, marker);
            });
            marker.setMap(map);

            this.parsedWayPoints.push({
              location: new google.maps.LatLng(
                e.point.coordinates[1],
                e.point.coordinates[0]
              ),
              stopover: true
            });
          }
        });
      }
    }
  },
  mounted() {
    this.initMap();
  },
  computed: {
    mapStyle() {
      return (
        "width: " + this.width + "px;height: " + this.height + this.heightUnits
      );
    },
    hasLatestPoint() {
      return (
        this.latestPoint &&
        this.latestPoint.lat !== "" &&
        !isNaN(this.latestPoint.lat) &&
        this.latestPoint.lat !== null
      );
    }
  }
};
</script>

<style scoped>
#map-canvas {
  height: 100%;
  width: 100%;
}
</style>
